"use client";

import React, { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { DoubleArrowDownIcon } from "@radix-ui/react-icons";
import { useMutation } from "@tanstack/react-query";
import { Button, Skeleton } from "@heroui/react";
import { useGetAccountInfo } from "@multiversx/sdk-dapp/hooks/account/useGetAccountInfo";

import { useGetAshswapAggregation } from "../../hooks/ashswap/useGetAshswapAggregation";
import { useRefreshAndSend } from "../../hooks/useRefreshAndSend";
import { Address } from "@multiversx/sdk-core";
import { getErrorMessage } from "../../utils";
import { A1X_TOKEN } from "../../config/sharedConfig";
import { AssetImage } from "../AssetImage";
import type { BuyA1XSchema } from "../../validations/buyA1XSchema";
import { useBuyA1XModal } from "../../contexts/BuyA1XModalContext";

const SwapMessages = {
  successMessage: "Buy order submitted successfully!",
  processingMessage: "Processing buy order...",
  errorMessage: "Failed to submit buy order",
};

export const ReviewBuyA1X = () => {
  const formValues = useWatch<BuyA1XSchema>();
  const { fromToken, amountDenominated, slippage } = formValues as BuyA1XSchema;
  const { closeModal } = useBuyA1XModal();

  const aggregationData = useGetAshswapAggregation(
    fromToken as any,
    A1X_TOKEN,
    amountDenominated,
    slippage
  );

  const refreshAndSend = useRefreshAndSend();
  const { address } = useGetAccountInfo();

  const buyMutation = useMutation({
    mutationFn: async () => {
      try {
        if (aggregationData.data?.getInteraction) {
          const interaction = await aggregationData.data.getInteraction(
            async () => Promise.resolve(true)
          );
          const transaction = interaction
            .withSender(new Address(address))
            .buildTransaction();
          const result = await refreshAndSend([transaction], SwapMessages);
          // Close modal after successful transaction send
          closeModal();
          return result;
        } else {
          throw new Error("No interaction available");
        }
      } catch (e) {
        console.error("Buy A1X transaction failed:", e);
        throw new Error(
          getErrorMessage(e as Error, "Could not build the buy transaction")
        );
      }
    },
  });

  const estimatedA1XAmount = useMemo(() => {
    return aggregationData.data?.sorResponse?.returnAmount ?? "0";
  }, [aggregationData.data]);

  const priceImpactPercent = useMemo(() => {
    return `${(
      (aggregationData.data?.sorResponse?.priceImpact ?? 0) * 100
    ).toFixed(2)}%`;
  }, [aggregationData.data]);

  const fromTokenTicker =
    fromToken?.ticker || fromToken?.identifier?.split("-")[0] || "";
  const exchangeRate = useMemo(() => {
    if (!amountDenominated || !estimatedA1XAmount) return "";
    return `1 ${fromTokenTicker} ≈ ${(
      parseFloat(estimatedA1XAmount) / parseFloat(amountDenominated)
    ).toFixed(6)} A1X`;
  }, [amountDenominated, estimatedA1XAmount, fromTokenTicker]);

  return (
    <div className="flex flex-col w-full gap-6">
      {/* From Token */}
      <div className="flex items-center gap-4 p-4 bg-white/5 dark:bg-white/5 rounded-xl">
        <AssetImage
          identifier={fromToken?.identifier || ""}
          className="w-10 h-10"
        />
        <div className="flex flex-col">
          <p className="text-sm text-gray-500 dark:text-gray-400">From</p>
          <p className="text-2xl font-bold text-black dark:text-white truncate max-w-[200px]" title={amountDenominated || "0"}>
            {amountDenominated || "0"}
          </p>
        </div>
      </div>

      {/* Arrow */}
      <div className="flex justify-center">
        <div className="p-2 bg-white/5 dark:bg-white/5 rounded-full">
          <DoubleArrowDownIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        </div>
      </div>

      {/* To Token (A1X) */}
      <div className="flex items-center gap-4 p-4 bg-white/5 dark:bg-white/5 rounded-xl">
        <AssetImage
          identifier={A1X_TOKEN}
          className="w-10 h-10"
        />
        <div className="flex flex-col">
          <p className="text-sm text-gray-500 dark:text-gray-400">To</p>
          {aggregationData.isLoading ? (
            <Skeleton className="w-24 h-6 rounded" />
          ) : (
            <p className="text-2xl font-bold text-black dark:text-white truncate max-w-[200px]" title={estimatedA1XAmount}>
              {estimatedA1XAmount}
            </p>
          )}
        </div>
      </div>

      {/* Transaction Details */}
      <div className="space-y-3 p-4 bg-white/5 dark:bg-white/5 rounded-xl">
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Exchange Rate
          </p>
          <p className="text-sm text-black dark:text-white">{exchangeRate}</p>
        </div>
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Price Impact
          </p>
          <p className="text-sm text-black dark:text-white">
            {priceImpactPercent}
          </p>
        </div>
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Slippage Tolerance
          </p>
          <p className="text-sm text-black dark:text-white">
            {(slippage / 100).toFixed(2)}%
          </p>
        </div>
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">Provider</p>
          <p className="text-sm text-black dark:text-white">AshSwap</p>
        </div>
      </div>

      {/* Buy Button */}
      <Button
        color="primary"
        className="w-full font-bold h-12 text-base"
        onPress={() => buyMutation.mutate()}
        disabled={buyMutation.isPending || aggregationData.isLoading}
        isLoading={buyMutation.isPending}
      >
        {buyMutation.isPending ? "Processing..." : "Buy A1X"}
      </Button>

      {/* Powered by AshSwap */}
      <div className="flex justify-center mt-2">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Powered by AshSwap
        </p>
      </div>
    </div>
  );
};
